import { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { analytics } from './api';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import MaintenanceGate from './components/public/MaintenanceGate';
import HomePage from './pages/HomePage';
import { getSubdomainFromHostname } from './utils/subdomain';
import { SITE_URL } from './api/core';
import { isPublicAnalyticsPath, normalizeAnalyticsPath } from './lib/analytics';

const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const ProtectedRoute = lazy(() => import('./components/admin/ProtectedRoute'));
const ErrorBoundary = lazy(() => import('./components/admin/ErrorBoundary').then((module) => ({ default: module.ErrorBoundary })));
const ProductsList = lazy(() => import('./pages/admin/ProductsList'));
const ProductForm = lazy(() => import('./pages/admin/product-form'));
const OrdersList = lazy(() => import('./pages/admin/orders/OrdersList'));
const UsersList = lazy(() => import('./pages/admin/UsersList'));
const CustomerDetail = lazy(() => import('./pages/admin/CustomerDetail'));
const OrderDetail = lazy(() => import('./pages/admin/orders/OrderDetail'));
const LegalPage = lazy(() => import('./pages/public/LegalPage'));
const LandingsList = lazy(() => import('./pages/admin/LandingsList'));
const LandingsForm = lazy(() => import('./pages/admin/landing-form'));
const LandingEditorPage = lazy(() => import('./pages/admin/LandingEditorPage'));
const InfluencersList = lazy(() => import('./pages/admin/influencers-list'));
const InfluencerFinances = lazy(() => import('./pages/admin/InfluencerFinances'));
const AdminCreatorsPage = lazy(() => import('./pages/admin/CreatorsPage'));
const StaffList = lazy(() => import('./pages/admin/StaffList'));
const SupportList = lazy(() => import('./pages/admin/SupportList'));
const MailboxPage = lazy(() => import('./pages/admin/MailboxPage'));
const MailboxBroadcastPage = lazy(() => import('./pages/admin/MailboxBroadcastPage'));
const MovementsList = lazy(() => import('./pages/admin/MovementsList'));
const ShippingList = lazy(() => import('./pages/admin/ShippingList'));
const MediaLibrary = lazy(() => import('./pages/admin/MediaLibrary'));
const HomeEditor = lazy(() => import('./pages/admin/home-editor'));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'));
const LegalPages = lazy(() => import('./pages/admin/LegalPages'));
const YunExpressHubPage = lazy(() => import('./pages/admin/yunexpress/YunExpressHubPage'));
const TestimonialsList = lazy(() => import('./pages/admin/TestimonialsList'));
const CouponsList = lazy(() => import('./pages/admin/CouponsList'));
const DocumentsPage = lazy(() => import('./pages/admin/DocumentsPage'));
const InfluencerDashboard = lazy(() => import('./pages/influencer/InfluencerDashboard'));
const InfluencerOrdersPage = lazy(() => import('./pages/influencer/InfluencerOrdersPage'));
const InfluencerCommentsPage = lazy(() => import('./pages/influencer/InfluencerCommentsPage'));
const InfluencerProfilePage = lazy(() => import('./pages/influencer/InfluencerProfilePage'));
const InfluencerDocumentsPage = lazy(() => import('./pages/influencer/InfluencerDocumentsPage'));
const InfluencerProtectedRoute = lazy(() => import('./components/influencer/InfluencerProtectedRoute'));
const InfluencerLayout = lazy(() => import('./components/influencer/InfluencerLayout'));
const ProductPage = lazy(() => import('./pages/public/ProductPage'));
const CategoryPage = lazy(() => import('./pages/public/CategoryPage'));
const RequestsPage = lazy(() => import('./pages/public/RequestsPage'));
const CreatorsPage = lazy(() => import('./pages/public/CreatorsPage'));
const CheckoutPage = lazy(() => import('./pages/public/CheckoutPage'));
const CheckoutResultPage = lazy(() => import('./pages/public/CheckoutResultPage'));
const ShopPage = lazy(() => import('./pages/public/ShopPage'));
const CustomerProfilePage = lazy(() => import('./pages/public/CustomerProfilePage'));
const InfluencerLandingPage = lazy(() => import('./pages/public/InfluencerLandingPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!isPublicAnalyticsPath(location.pathname) || getSubdomainFromHostname() || location.pathname.startsWith('/creator/')) return;
    const path = normalizeAnalyticsPath(location.pathname, location.search);
    const query = new URLSearchParams(location.search);

    analytics.track({
      eventType: 'VIEW',
      entityType: 'PAGE',
      entityId: path,
      metadata: {
        path,
        title: document.title,
        referrer: document.referrer,
        utmSource: query.get('utm_source'),
        utmMedium: query.get('utm_medium'),
        utmCampaign: query.get('utm_campaign'),
      },
    });
  }, [location.pathname, location.search]);

  return null;
}

function RouteFallback() {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="h-8 w-8 rounded-full border-4 border-[#FF50A3] border-t-transparent animate-spin" />
    </div>
  );
}

function MainDomainRedirect() {
  const location = useLocation();

  useEffect(() => {
    window.location.replace(`${SITE_URL}${location.pathname}${location.search}${location.hash}`);
  }, [location.pathname, location.search, location.hash]);

  return <RouteFallback />;
}

function AppRoutes() {
  const [detectedSubdomain, setDetectedSubdomain] = useState(null);

  useEffect(() => {
    const sub = getSubdomainFromHostname();
    if (sub) setDetectedSubdomain(sub);
  }, []);

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {detectedSubdomain ? (
        <>
          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/checkout" element={<MainDomainRedirect />} />
          <Route path="/checkout/*" element={<MainDomainRedirect />} />
          <Route path="/my-account" element={<MainDomainRedirect />} />
          <Route path="/my-account/:tab" element={<MainDomainRedirect />} />
          <Route path="/merch" element={<ShopPage />} />
          <Route path="/tienda" element={<Navigate to="/merch" replace />} />
          <Route path="*" element={<InfluencerLandingPage subdomain={detectedSubdomain} />} />
        </>
      ) : (
        <>
          <Route path="/" element={<HomePage />} />
          <Route path="/merch" element={<ShopPage />} />
          <Route path="/requests" element={<RequestsPage />} />
          <Route path="/creators" element={<CreatorsPage />} />
          <Route path="/tienda" element={<Navigate to="/merch" replace />} />
          <Route path="/category/:slug" element={<CategoryPage />} />
          <Route path="/product/:slug" element={<ProductPage />} />
           <Route path="/checkout" element={<CheckoutPage />} />
           <Route path="/checkout/result" element={<CheckoutResultPage />} />
          <Route path="/my-account" element={<CustomerProfilePage />} />
          <Route path="/my-account/:tab" element={<CustomerProfilePage />} />
          <Route path="/creator/:subdomain" element={<InfluencerLandingPage />} />
          <Route path="/contacto" element={<ContactPage />} />
          <Route path="/soporte" element={<Navigate to="/contacto" replace />} />
          <Route path="/terminos-y-condiciones" element={<LegalPage />} />
          <Route path="/politica-de-privacidad" element={<LegalPage />} />
          <Route path="/envios-y-entregas" element={<LegalPage />} />
          <Route path="/preguntas-frecuentes" element={<LegalPage />} />
          <Route path="/cambios-y-devoluciones" element={<LegalPage />} />
          <Route path="/libro-de-reclamaciones" element={<LegalPage />} />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <ThemeProvider>
                  <AdminLayout />
                </ThemeProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="products" element={<ProductsList />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/:id/edit" element={<ProductForm />} />
            <Route path="categories" element={<Navigate to="products" replace />} />
            <Route path="orders" element={<OrdersList />} />
            <Route path="users" element={<UsersList />} />
            <Route path="users/:id" element={<CustomerDetail />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="creators" element={<AdminCreatorsPage />} />
            <Route path="creators/:creatorId/:tab" element={<AdminCreatorsPage />} />
            <Route path="influencers" element={<InfluencersList />} />
            <Route path="influencer-finances" element={<InfluencerFinances />} />
            <Route path="landings" element={<LandingsList />} />
            <Route path="landings/new" element={<ErrorBoundary><LandingsForm /></ErrorBoundary>} />
            <Route path="landings/:id" element={<ErrorBoundary><LandingsForm /></ErrorBoundary>} />
            <Route path="landings/:id/editor" element={<ErrorBoundary><LandingEditorPage /></ErrorBoundary>} />
            <Route path="merch" element={<Navigate to="products" replace />} />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="home" element={<HomeEditor />} />
            <Route path="settings/banners" element={<Navigate to="home" replace />} />
            <Route path="influencers" element={<InfluencersList />} />
            <Route path="influencer-finances" element={<InfluencerFinances />} />
            <Route path="staff" element={<StaffList />} />
              <Route path="support" element={<SupportList />} />
              <Route path="mailbox" element={<MailboxPage />} />
              <Route path="mailbox/broadcast" element={<MailboxBroadcastPage />} />
            <Route path="shipping" element={<ShippingList />} />
            <Route path="movements" element={<MovementsList />} />
            <Route path="legal-pages" element={<LegalPages />} />
            <Route path="yunexpress" element={<YunExpressHubPage />} />
            <Route path="yunexpress-logistics" element={<Navigate to="/admin/yunexpress?section=logistica" replace />} />
            <Route path="testimonials" element={<TestimonialsList />} />
            <Route path="coupons" element={<CouponsList />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          <Route
            path="/influencer"
            element={
              <InfluencerProtectedRoute>
                <ThemeProvider>
                  <InfluencerLayout />
                </ThemeProvider>
              </InfluencerProtectedRoute>
            }
          >
            <Route index element={<InfluencerProtectedRoute redirectToDefault />} />
            <Route path="dashboard" element={<InfluencerDashboard />} />
            <Route path="orders" element={<InfluencerProtectedRoute module="ORDERS"><InfluencerOrdersPage /></InfluencerProtectedRoute>} />
            <Route path="comments" element={<InfluencerCommentsPage />} />
            <Route path="profile" element={<InfluencerProfilePage />} />
            <Route path="documents" element={<InfluencerDocumentsPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      )}
    </Routes>
    </Suspense>
  );
}

function App() {
  const basename = window.location.pathname === '/en' || window.location.pathname.startsWith('/en/') ? '/en' : '/';
  return (
    <BrowserRouter basename={basename}>
      <AuthProvider>
        <LanguageProvider>
        <ToastProvider>
        <MaintenanceGate>
        <PageViewTracker />
        <AppRoutes />
        </MaintenanceGate>
        </ToastProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
